/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * TdAccount — billing / account info card with an F16 identity-fingerprint
 * section underneath. The fingerprint shows only while the vault is
 * unlocked AND there's an active identity; vault `locked`/`unlocked` events
 * dispatched on `document` (single source = `vaultService`, #111) trigger
 * a re-render to add or drop the section.
 *
 * Lit light-DOM component (#206). Per-field values reach the template via
 * `${...}` text-bindings (always `textContent`, never HTML), so neither
 * billing strings nor the formatted fingerprint can smuggle markup —
 * matching the safety guarantee of the previous DOMParser path.
 */

import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { fetchAccountData, type AccountData } from '../services/dashboard.js';
import {
  vaultService,
  VAULT_UNLOCKED_EVENT,
  VAULT_LOCKED_EVENT,
} from '../services/vault.js';
import { formatFingerprint } from '../services/keys/fingerprint.js';
import { t, LocaleController } from '../services/i18n.js';

@customElement('td-account')
export class TdAccount extends LitElement {
  @state() accountData: AccountData | undefined = undefined;

  /** Re-renders this component when the UI locale changes (#89). */
  readonly localeController = new LocaleController(this);

  // Vault state lives outside the component (single source = vaultService).
  // Vault events fire `requestUpdate()` so the next render re-reads the
  // active identity. No mirror @state field — there is nothing to keep
  // in sync; vaultService stays authoritative.
  private readonly _onVaultStateChanged: () => void = () =>
    this.requestUpdate();

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    console.log('td-account: Component connected, loading account data...');
    document.addEventListener(VAULT_UNLOCKED_EVENT, this._onVaultStateChanged);
    document.addEventListener(VAULT_LOCKED_EVENT, this._onVaultStateChanged);
    void this.loadAccountData();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener(
      VAULT_UNLOCKED_EVENT,
      this._onVaultStateChanged
    );
    document.removeEventListener(VAULT_LOCKED_EVENT, this._onVaultStateChanged);
  }

  /**
   * Resolve the active identity's signing public key when the vault is
   * unlocked. Returns null in any other state — render will skip the
   * fingerprint section silently rather than show a broken affordance.
   */
  _activeSigningKey(): Uint8Array | null {
    if (!vaultService.isUnlocked()) return null;
    const identity = vaultService.getActiveIdentity();
    if (!identity) return null;
    return identity.sig?.publicKey ?? null;
  }

  async loadAccountData(): Promise<void> {
    console.log('td-account: Fetching account data from API...');
    this.accountData = await fetchAccountData();
    console.log('td-account: Account data loaded:', this.accountData);
  }

  private _fingerprintTemplate(): TemplateResult | typeof nothing {
    const signingKey = this._activeSigningKey();
    if (!signingKey) return nothing;
    const formatted = formatFingerprint(signingKey);
    return html`
      <hr />
      <h3>${t('account.fingerprintTitle')}</h3>
      <p class="td-fingerprint" data-test="fingerprint-value">${formatted}</p>
      <wa-copy-button value=${formatted} data-test="fingerprint-copy">
        ${t('account.copyFingerprint')}
      </wa-copy-button>
      <p class="td-hint">${t('account.fingerprintHint')}</p>
    `;
  }

  override render(): TemplateResult {
    const data = this.accountData;
    return html`
      <wa-card>
        <h2>${t('account.title')}</h2>
        ${
          data
            ? html`
                <p class="account-level">
                  ${t('account.level', { level: data.level })}
                </p>
                <p class="account-next-billing">
                  ${t('account.nextBilling', { date: data.nextBilling })}
                </p>
                <p class="account-waiting">
                  ${t('account.waiting', { count: data.waitingAcceptance })}
                </p>
                <p class="account-pending">
                  ${t('account.pending', { count: data.pendingAcceptance })}
                </p>
              `
            : html`<p class="account-status">${t('account.loading')}</p>`
        }
        <div class="fingerprint-section">${this._fingerprintTemplate()}</div>
      </wa-card>
    `;
  }
}
